import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Col, Drawer, Popconfirm, Row, Space, Table, Tag, message } from 'antd'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { DateTime, LocalDateTime } from '~/components/LocalDateTime'
import { AWS_REGIONS } from '~/utils/constants'
import { showErrors } from '~/utils/helpers'
import { ServerForm } from './ServerForm'
import { useDeleteServerMutation, useSyncServersMutation, useSyncSingleServerMutation } from './mutation'
import { useCourseServersQuery } from './query'
import { useCourseQuery } from '../query'

// eslint-disable-next-line react/prop-types
const ServerList: React.FC<{ showSyncButton: boolean }> = ({ showSyncButton }) => {
  const { id } = useParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const queryClient = useQueryClient()
  const deleteMutation = useDeleteServerMutation()
  const syncServerMutation = useSyncServersMutation(Number(id))
  const syncSingleServerMutation = useSyncSingleServerMutation()
  const { data: course } = useCourseQuery(Number(id))

  const [isSyncing, setIsSyncing] = useState(false)
  const { data, isLoading } = useCourseServersQuery(id, isSyncing)

  useEffect(() => {
    setIsSyncing(
      data.some(i => i.state != null && i.state !== 'READY')
    )
  }, [data])

  if (course == null) {
    return null
  }

  return (
    <>
      <Table
        title={() =>
          <Row>
            <Col flex="200px">
              <h3>Servers</h3>
            </Col>
            <Col flex="auto">
              <Row justify="end">
                <Space>
                  <Button type="primary" key="2" onClick={() => { setIsDrawerOpen(true) }}>
                    New server
                  </Button>
                  {showSyncButton && <Popconfirm
                    key="sync_servers"
                    title={'Do you want to sync all servers from the master server?'}
                    okText="YES"
                    onConfirm={() => {
                      syncServerMutation.mutate(Number(id), {
                        onSuccess: () => {
                          message.success('Start syncing server successfully')
                          queryClient.invalidateQueries({ queryKey: ['courses', id, 'servers'] })
                        },
                        onError: showErrors
                      })
                    }}
                  >
                    <Button loading={isSyncing} type='primary'>Sync servers</Button>
                  </Popconfirm>}
                </Space>
              </Row>
            </Col >
          </Row >
        }
        size="small"
        dataSource={data}
        rowKey='id'
        loading={isLoading}
        pagination={false}
        footer={() => (
          <Row justify="start">
            <span>Total: {data.length} servers</span>
          </Row>
        )}
        columns={
          [
            {
              title: 'Id',
              key: 'id',
              dataIndex: 'id'
            },
            {
              title: 'Server Name',
              key: 'server_name',
              dataIndex: 'name'
            },
            {
              title: 'Location',
              key: 'location',
              render: item => AWS_REGIONS.find(i => i.code === item.region_code)?.name
            },
            {
              title: 'EC2 Instance ID',
              key: 'private_ip_address',
              dataIndex: 'ec2_instance_id'
            },
            {
              title: 'Private IP Address',
              key: 'private_ip_address',
              dataIndex: 'ec2_private_ip_address'
            },
            {
              title: 'Is Master',
              key: 'is_master',
              dataIndex: 'is_master',
              render: (value) => {
                return (value as boolean) ? <Tag color="var(--ant-primary-color)">Master</Tag> : null
              }
            },
            {
              title: 'State',
              key: 'state',
              render: item => {
                const colorMap = {
                  READY: 'green',
                  WAITING_FOR_SYNCING: 'orange',
                  SYNCING: 'blue',
                  ERROR: 'red'
                }

                const state = item.state ?? 'READY'
                const color = colorMap[state] ?? 'default'

                return <div>
                  <Space>
                    <Tag color={color}>{state}</Tag>
                    {state === 'ERROR' &&
                      <Button onClick={() => {
                        syncSingleServerMutation.mutate(item.id, {
                          onSuccess: () => {
                            message.success('Start retrying')
                          },
                          onError: showErrors
                        })
                      }}>Retry</Button>}
                  </Space>
                </div >
              }
            },
            {
              title: 'Last Sync',
              key: 'last_synced_at',
              dataIndex: 'last_synced_at',
              render: (value) => {
                if (value == null || value.length === 0) {
                  return '-'
                }

                if (course.timezone == null || course.timezone.length === 0) {
                  return <DateTime date={value} />
                }

                return (
                  <LocalDateTime
                    date={value}
                    timezone={course.timezone}
                  />
                )
              }
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (item: { name: string, id: number }) => {
                return (
                  <Space size="middle">
                    <Button type="primary" icon={<EditOutlined />} onClick={() => {
                      setSelected(item)
                      setIsDrawerOpen(true)
                    }} />
                    <Popconfirm
                      title={`Do you want to delete server ${item.name}?`}
                      onConfirm={() => {
                        deleteMutation.mutate(item.id, {
                          onSuccess: () => {
                            queryClient.invalidateQueries({ queryKey: ['courses', id] })
                          },
                          onError: showErrors
                        })
                      }}
                      okText="YES"
                    >
                      <Button danger type='primary' icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )
              }
            }
          ]}
      />

      <Drawer
        open={isDrawerOpen}
        size={720}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelected(null)
        }}
        destroyOnHidden
      >
        <ServerForm
          courseId={Number(id)}
          server={selected}
          onSuccess={() => {
            setIsDrawerOpen(false)
            setSelected(null)
            queryClient.invalidateQueries({ queryKey: ['courses', id] })
          }}
        />
      </Drawer >
    </>
  )
}

export default ServerList
